import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import "../assets/css/Signin.css";
import "../assets/css/ForgotPassword.css";
import axiosInstance from "../components/api/axiosInstance";
import { PASSWORD_POLICY_HINT, validatePasswordStrength } from "../utils/passwordPolicy";
import AuthShell, { AuthBrandMark } from "../components/AuthShell";
import PasswordInput from "../components/PasswordInput";

function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [formData, setFormData] = useState({
    newPassword: "",
    confirmPassword: "",
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [tokenValid, setTokenValid] = useState(null);

  useEffect(() => {
    if (!token) {
      setTokenValid(false);
      setError("Invalid or missing reset token. Please request a new password reset link.");
    } else {
      setTokenValid(true);
    }
  }, [token]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (error) setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!formData.newPassword || !formData.confirmPassword) {
      setError("All fields are required");
      return;
    }

    const strengthError = validatePasswordStrength(formData.newPassword);
    if (strengthError) {
      setError(strengthError);
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (!token) {
      setError("Invalid reset token. Please request a new password reset link.");
      return;
    }

    setLoading(true);

    try {
      const response = await axiosInstance.post("/reset-password", {
        reset_token: token,
        new_password: formData.newPassword,
      });

      if (response.data && response.data.status === "success") {
        const successMessage =
          response.data.message || "Password reset successfully! Redirecting to login...";
        setSuccess(successMessage);
        setFormData({
          newPassword: "",
          confirmPassword: "",
        });

        setTimeout(() => {
          navigate("/");
        }, 2000);
      } else {
        const errorMsg = response.data?.message || "Failed to reset password. Please try again.";
        setError(errorMsg);
      }
    } catch (err) {
      if (err.response && err.response.data) {
        setError(
          err.response.data.message ||
            err.response.data.error ||
            `Server error: ${err.response.status}`
        );
      } else if (err.request) {
        setError("Network error. Please check your connection and try again.");
      } else {
        setError("Failed to reset password. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  if (tokenValid === false) {
    return (
      <AuthShell
        eyebrow="Staff access for"
        subtitle="Reset your Cloud Office password securely."
      >
        <AuthBrandMark logo="office" />
        <h2 className="signin-heading">Invalid Reset Link</h2>
        <p className="signin-subheading">
          {error || "This password reset link is invalid or has expired."}
        </p>
        <button
          type="button"
          className="signin-submit"
          onClick={() => navigate("/forgot-password")}
        >
          Request New Reset Link
        </button>
        <div className="signin-forgot" style={{ textAlign: "center", marginTop: 8 }}>
          <button type="button" className="signin-forgot-btn" onClick={() => navigate("/")}>
            Back to Login
          </button>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      eyebrow="Staff access for"
      subtitle="Reset your Cloud Office password securely."
    >
      <AuthBrandMark logo="office" />
      <form className="signin-form" onSubmit={handleSubmit}>
        <h2 className="signin-heading">Reset Password</h2>
        <p className="signin-subheading">Enter your new password below.</p>

        {error && <p className="signin-error">{error}</p>}
        {success && <p className="forgotpassword-success">{success}</p>}

        <div className="signin-field">
          <label htmlFor="reset-new-password">New Password</label>
          <PasswordInput
            id="reset-new-password"
            name="newPassword"
            value={formData.newPassword}
            onChange={handleInputChange}
            required
            disabled={loading}
          />
          <span className="hint-text">{PASSWORD_POLICY_HINT}</span>
        </div>

        <div className="signin-field">
          <label htmlFor="reset-confirm-password">Confirm New Password</label>
          <PasswordInput
            id="reset-confirm-password"
            name="confirmPassword"
            value={formData.confirmPassword}
            onChange={handleInputChange}
            required
            disabled={loading}
          />
        </div>

        <button type="submit" className="signin-submit" disabled={loading || tokenValid === null}>
          {loading ? "Resetting..." : "Reset Password"}
        </button>

        <div className="signin-forgot" style={{ textAlign: "center", marginTop: 8 }}>
          <button
            type="button"
            className="signin-forgot-btn"
            onClick={() => navigate("/")}
            disabled={loading}
          >
            Back to Login
          </button>
        </div>
      </form>
    </AuthShell>
  );
}

export default ResetPassword;
